"use client";

import { useState } from "react";
import Button, { type ButtonProps } from "src/components/common/Button/Button";
import {
  startRouteProgress,
  stopRouteProgress,
} from "src/components/common/RouteProgress/RouteProgressStore";
import { useAuth } from "src/lib/contexts/AuthContext";
import { useAppRouter } from "src/lib/Navigation";
import { useExtracted } from "#i18n";

export type SignOutButtonProps = Partial<ButtonProps> & { redirectTo?: string };

const SignOutButton = (props: SignOutButtonProps) => {
  const translateMessage = useExtracted();
  const { onClick, redirectTo = "/", ...otherProps } = props;
  const auth = useAuth();
  const router = useAppRouter();
  const [submitting, setSubmitting] = useState(false);

  const signOut = async () => {
    // Loaded on click: error pages (part of every page's shell) render this button.
    const [{ postAuthSignOut }, { clearAuthCache }] = await Promise.all([
      import("src/CalmLensApi"),
      import("src/lib/AuthCache"),
    ]);
    await postAuthSignOut.submit({});
    await clearAuthCache();
    auth.setAuthState(undefined);
  };

  return (
    <Button
      label={translateMessage("Sign out")}
      variant={"outline"}
      loading={submitting}
      {...otherProps}
      onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
        setSubmitting(true);
        try {
          onClick?.(e);
          startRouteProgress();
          await signOut();
          router.replace(redirectTo);
          stopRouteProgress();
        } catch (error) {
          stopRouteProgress();
          setSubmitting(false);
          throw error;
        }
      }}
    />
  );
};

export default SignOutButton;
